import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect } from "react";
import { ThemeProvider } from "./contexts/ThemeContext";
import { authFetch } from "./services/authService";

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Appointments from "./pages/Appointments";
import VehicleAcceptance from "./pages/VehicleAcceptance";
import Quotes from "./pages/Quotes";
import Invoices from "./pages/Invoices";
import SupplierOrders from "./pages/SupplierOrders";
import PartsRequests from "./pages/PartsRequests";
import SupplierResponse from "./pages/SupplierResponse";
import Archive from "./pages/Archive";
import NewsFeed from "./pages/NewsFeed";
import Profile from "./pages/Profile";
import Customers from "./pages/Customers";
import Suppliers from "./pages/Suppliers";
import Vehicles from "./pages/Vehicles";
import Export from "./pages/Export";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUsersNew from "./pages/admin/AdminUsersNew";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminCommunications from "./pages/admin/AdminCommunications";
import AdminActivity from "./pages/admin/AdminActivity";
import AdminCampaigns from "./pages/admin/AdminCampaigns";
import AdminCampaignDetail from "./pages/admin/AdminCampaignDetail";
import AdminAuditLogs from "./pages/admin/AdminAuditLogs";
import AdminModules from "./pages/admin/AdminModules";
import AdminPreventivoVeloce from "./pages/admin/AdminPreventivoVeloce";
import AdminSupportRequests from "./pages/admin/AdminSupportRequests";
import AdminYouTubeChannels from "./pages/admin/AdminYouTubeChannels";
import AdminBackups from "./pages/admin/AdminBackups";
import AdminQuoteStats from "./pages/admin/AdminQuoteStats";
import AdminRicambisti from "./pages/AdminRicambisti";
import CreditNotes from "./pages/CreditNotes";
import RicambistaDashboard from "./pages/RicambistaDashboard";
import RicambistaRichieste from "./pages/RicambistaRichieste";
import RicambistaOperators from "./pages/RicambistaOperators";
import RicambistaOfficine from "./pages/RicambistaOfficine";
import RicambistaOrdini from "./pages/RicambistaOrdini";
import Settings from "./pages/Settings";
import PointsPage from "./pages/PointsPage";
import VouchersPage from "./pages/VouchersPage";
import Magazzino from "./pages/Magazzino";
import StatisticheAvanzate from "./pages/StatisticheAvanzate";
import MarginiProfitto from "./pages/MarginiProfitto";
import Gommista from "./pages/Gommista";
import DDT from "./pages/DDT";
import FiscalModule from "./pages/FiscalModule";
import FiscalDashboard from "./pages/FiscalDashboard";
import WorkOrders from "./pages/WorkOrders";
import WorkSchedule from "./pages/WorkSchedule";
import WorkComplete from "./pages/WorkComplete";
import PreventivoVeloce from "./pages/PreventivoVeloce";
import PrintRequest from "./pages/PrintRequest";
import PublicQuote from "./pages/PublicQuote";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Rentri from "./pages/Rentri";
import VideoRiparazioni from "./pages/VideoRiparazioni";

// Components
import DashboardLayout from "./components/DashboardLayout";
import AdminLayout from "./components/AdminLayout";
import RicambistaLayout from "./components/RicambistaLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Component to handle /reset-password?token=XXX route
function ResetPasswordRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      // Redirect to landing page with reset_token parameter
      navigate(`/?reset_token=${token}`, { replace: true });
    } else {
      // No token, redirect to home
      navigate("/", { replace: true });
    }
  }, [searchParams, navigate]);
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="spinner"></div>
    </div>
  );
}

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`);

        if (!response.ok) {
          throw new Error("Not authenticated");
        }

        const userData = await response.json();
        
        // Check admin/staff access for admin routes
        if (requireAdmin) {
          const role = (userData.role || "user").toLowerCase();
          if (role !== "admin" && role !== "staff") {
            setIsAuthenticated(false);
            return;
          }
        }

        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [requireAdmin, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return typeof children === "function" ? children({ user }) : children;
};

// App Router Component
function AppRouter() {
  return (
    <Routes>
      {/* Public Route */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Password Reset Route - Redirects to landing with token */}
      <Route path="/reset-password" element={<ResetPasswordRedirect />} />
      
      {/* Public Supplier Response Page (no auth required) */}
      <Route path="/risposta-ricambi/:token" element={<SupplierResponse />} />
      
      {/* Preventivo Veloce PWA - Public route with token */}
      <Route path="/r/:token" element={<PreventivoVeloce />} />
      <Route path="/r/" element={<PreventivoVeloce />} />
      <Route path="/r" element={<PreventivoVeloce />} />
      
      {/* Preventivo Veloce Print Page - Public, no auth */}
      <Route path="/print/:requestId" element={<PrintRequest />} />

      {/* Public Quote (shareable link with PDF download) - no auth */}
      <Route path="/preventivo/:token" element={<PublicQuote />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Protected User Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Dashboard user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Appointments user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/acceptances"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <VehicleAcceptance user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Quotes user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Invoices user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/credit-notes"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <CreditNotes user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-orders"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <SupplierOrders user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/work-orders"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <WorkOrders user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/work-schedule"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <WorkSchedule user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/work-complete/:token"
        element={<WorkComplete />}
      />
      <Route
        path="/parts-requests"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <PartsRequests user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/archive"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Archive user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/news"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <NewsFeed user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Profile user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Customers user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Suppliers user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Vehicles user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/export"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Export user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/points"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <PointsPage user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/vouchers"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <VouchersPage user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* Moduli Estesi Routes */}
      <Route
        path="/magazzino"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Magazzino user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/statistiche"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <StatisticheAvanzate user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/margini"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <MarginiProfitto user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/gommista"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Gommista user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ddt"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <DDT user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/fiscale"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <FiscalModule user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/fiscale/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <FiscalDashboard user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/rentri"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Rentri user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/video-riparazioni"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <VideoRiparazioni user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminDashboard user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/brands"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminBrands user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/communications"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminCommunications user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/activity"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminActivity user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminUsersNew user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminUserDetail user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminCampaigns user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns/:campaignId"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminCampaignDetail user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminAuditLogs user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/modules"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminModules user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/youtube-channels"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminYouTubeChannels user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* Admin Ricambisti/Officine Management */}
      <Route
        path="/admin/ricambisti"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminRicambisti user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      
      {/* Admin Preventivo Veloce */}
      <Route
        path="/admin/preventivo-veloce"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminPreventivoVeloce user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />
      
      {/* Admin Support Requests */}
      <Route
        path="/admin/support-requests"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminSupportRequests user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* Admin Backups & GDPR */}
      <Route
        path="/admin/backups"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminBackups user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* Admin Quote Stats */}
      <Route
        path="/admin/quote-stats"
        element={
          <ProtectedRoute requireAdmin>
            {({ user }) => (
              <AdminLayout user={user}>
                <AdminQuoteStats user={user} />
              </AdminLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* Ricambista Routes */}
      <Route
        path="/ricambista"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RicambistaLayout user={user}>
                <RicambistaDashboard user={user} />
              </RicambistaLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ricambista/richieste"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RicambistaLayout user={user}>
                <RicambistaRichieste user={user} />
              </RicambistaLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ricambista/operatori"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RicambistaLayout user={user}>
                <RicambistaOperators user={user} />
              </RicambistaLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ricambista/officine"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RicambistaLayout user={user}>
                <RicambistaOfficine user={user} />
              </RicambistaLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ricambista/ordini-gestione"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RicambistaLayout user={user}>
                <RicambistaOrdini user={user} />
              </RicambistaLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* Settings Route - Officina Only */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <DashboardLayout user={user}>
                <Settings user={user} />
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </ThemeProvider>
  );
}

export default App;
